// Clipboard helper that works on INSECURE origins (plain HTTP, non-localhost).
//
// Browsers gate `navigator.clipboard` behind a secure context (HTTPS or
// localhost). When the app is served over http://<lan-ip>, navigator.clipboard
// is `undefined`, so `navigator.clipboard?.writeText(...)` silently no-ops and
// the copy "succeeds" visually while the clipboard stays empty (the Copy-button
// bug). This helper tries the modern API first, then falls back to the legacy
// hidden-textarea + execCommand('copy') path, which still works on HTTP.
//
// Returns true only if the copy actually went through — callers should gate
// their "Copied ✓" feedback on this.

export async function copyText(text: string): Promise<boolean> {
  // Secure-context path (HTTPS / localhost)
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // permission denied / not allowed → fall through to legacy path
  }

  // Legacy fallback — works on insecure (HTTP) origins.
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    // Keep it off-screen but selectable; avoid scroll jump on focus.
    ta.style.position = 'fixed'
    ta.style.top = '0'
    ta.style.left = '0'
    ta.style.width = '1px'
    ta.style.height = '1px'
    ta.style.padding = '0'
    ta.style.border = 'none'
    ta.style.outline = 'none'
    ta.style.boxShadow = 'none'
    ta.style.background = 'transparent'
    ta.style.opacity = '0'
    ta.setAttribute('readonly', '')
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    ta.setSelectionRange(0, text.length)
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}
